import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapView } from "./components/MapView";
import { DailyView, isTodayDailyUnplayed } from "./components/DailyView";
import { todayOffset } from "./lib/daily";
import { normalize } from "./lib/normalize";
import {
  loadBordersVisible,
  loadFinished,
  loadGuesses,
  loadLegacyChkoNpFinished,
  loadLegacyChkoNpGuesses,
  loadMode,
  saveBordersVisible,
  saveFinished,
  saveGuesses,
  saveMode,
} from "./lib/persistence";
import { useDataset } from "./lib/useDataset";
import { useDaily } from "./lib/useDaily";
import {
  DATASETS,
  METRO_LINE_COLORS,
  PROTECTED_AREA_COLORS,
  PROTECTED_AREA_KINDS,
  PROTECTED_AREA_STAT_LABELS,
  type BBox,
  type Entity,
  type Mode,
  type ProtectedAreaKind,
} from "./lib/types";

type SortMode = "recent" | "population" | "alpha";
type AppView = "map" | "daily";

/**
 * Hidden dev route for previewing any daily entry: `/daily-test/<index>`
 * opens the daily view pinned to that raw index into `daily.json`,
 * bypassing the "no peeking at the future" date gating.
 */
function parseDailyTestIndex(): number | null {
  const m = /^\/daily-test\/(\d+)\/?$/.exec(window.location.pathname);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export function App() {
  const initialDailyTestIndex = useMemo(() => parseDailyTestIndex(), []);
  const [view, setView] = useState<AppView>(initialDailyTestIndex !== null ? "daily" : "map");
  const [dailyTestMode, setDailyTestMode] = useState(initialDailyTestIndex !== null);
  const [dailyOffset, setDailyOffset] = useState(() => initialDailyTestIndex ?? todayOffset());
  const [dailyGlow, setDailyGlow] = useState(() => isTodayDailyUnplayed());
  const dailyState = useDaily();
  const [mode, setMode] = useState<Mode>(() => loadMode("obce"));
  const datasetState = useDataset(mode);

  const [guesses, setGuesses] = useState<Set<string>>(() => new Set());
  // Order in which IDs were guessed (newest first), separate from the
  // set so the "recent" sort is stable.
  const [order, setOrder] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [bad, setBad] = useState(false);
  const [flyTo, setFlyTo] = useState<{ bbox: BBox; key: number } | null>(null);
  const [latestId, setLatestId] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>("recent");
  const [bordersVisible, setBordersVisible] = useState<boolean>(() =>
    loadBordersVisible(true),
  );
  // When `finished` is true the player has called the run done.
  // Submitting is locked out and the map reveals names of un-guessed
  // entities on hover. Per-mode, persisted alongside guesses.
  const [finished, setFinished] = useState<boolean>(false);
  // Which mode's guesses are currently reflected in `guesses`?
  // null while the new mode's index is loading or hydrating, so the
  // persistence effect won't save the empty set into a different
  // mode's storage key during a mode switch.
  const [hydratedFor, setHydratedFor] = useState<Mode | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Kept in sync each render so the global keydown handler can submit
  // the current string without re-attaching the listener on every
  // keystroke.
  const inputValueRef = useRef(input);
  inputValueRef.current = input;

  // ── Persistence: settings ───────────────────────────────────────────
  useEffect(() => {
    saveMode(mode);
  }, [mode]);
  useEffect(() => {
    saveBordersVisible(bordersVisible);
  }, [bordersVisible]);

  // ── Mode switch: clear transient state immediately ──────────────────
  useEffect(() => {
    setGuesses(new Set());
    setOrder([]);
    setInput("");
    setLatestId(null);
    setFlyTo(null);
    setBad(false);
    setFinished(false);
    setHydratedFor(null);
  }, [mode]);

  // ── Hydrate guesses + finished flag once the dataset is ready ───────
  useEffect(() => {
    if (datasetState.status !== "ready") return;
    const { descriptor, byId } = datasetState.data;
    const validIds = new Set(byId.keys());
    const stored = loadGuesses(descriptor.storageKey).filter((id) => validIds.has(id));
    const legacy =
      descriptor.mode === "chkonp" ? loadLegacyChkoNpGuesses(validIds) : [];
    const merged = [...new Set([...stored, ...legacy])];
    setGuesses(new Set(merged));
    setOrder(merged);
    const finished =
      loadFinished(descriptor.storageKey) ||
      (descriptor.mode === "chkonp" && loadLegacyChkoNpFinished());
    setFinished(finished);
    setHydratedFor(descriptor.mode);
    inputRef.current?.focus();
  }, [datasetState]);

  // ── Persist guesses + finished (only for the hydrated mode) ─────────
  useEffect(() => {
    if (hydratedFor !== mode) return;
    saveGuesses(DATASETS[mode].storageKey, guesses);
  }, [guesses, hydratedFor, mode]);
  useEffect(() => {
    if (hydratedFor !== mode) return;
    saveFinished(DATASETS[mode].storageKey, finished);
  }, [finished, hydratedFor, mode]);

  // ── Sanity: the "sort by population" mode is meaningless when the
  //    descriptor has no secondary metric (metro). Snap it back to
  //    "recent" so the dial doesn't lie about the current order.
  useEffect(() => {
    if (DATASETS[mode].secondaryStat === "none" && sortMode === "population") {
      setSortMode("recent");
    }
  }, [mode, sortMode]);

  // ── Submit a guess ──────────────────────────────────────────────────
  // We deliberately never call `setFlyTo` here: auto-zooming is jarring
  // when typing in quick succession and yanks the user out of the area
  // they were studying. The new guess shows up as green where it is;
  // if the user wants a closer look they click the sidebar row.
  const submit = useCallback(
    (value: string) => {
      if (datasetState.status !== "ready") return;
      if (finished) return;
      const key = normalize(value);
      if (!key) return;
      const matches = datasetState.data.byNormalizedName.get(key);
      if (!matches || matches.length === 0) {
        setBad(true);
        setTimeout(() => setBad(false), 380);
        return;
      }
      // All matches with this normalized name count (e.g. there are
      // dozens of "Lhota" – the player typed the word, so they "knew"
      // them all). Compute additions outside the state setter because
      // React StrictMode invokes updaters twice in development.
      const newly = matches.filter((m) => !guesses.has(m.id));
      if (newly.length === 0) {
        setLatestId(matches[0]!.id);
        setInput("");
        return;
      }
      setGuesses((prev) => {
        const next = new Set(prev);
        for (const m of newly) next.add(m.id);
        return next;
      });
      setOrder((prev) => [...newly.map((m) => m.id).reverse(), ...prev]);
      setLatestId(newly[0]!.id);
      setInput("");
    },
    [datasetState, guesses, finished],
  );

  const reset = useCallback(() => {
    if (datasetState.status !== "ready") return;
    if (!window.confirm(datasetState.data.descriptor.resetConfirm)) return;
    setGuesses(new Set());
    setOrder([]);
    setLatestId(null);
    setInput("");
    setFinished(false);
    inputRef.current?.focus();
  }, [datasetState]);

  const finish = useCallback(() => {
    if (datasetState.status !== "ready" || finished) return;
    if (!window.confirm(datasetState.data.descriptor.finishConfirm)) return;
    setFinished(true);
    setInput("");
  }, [datasetState, finished]);

  // ── Totals / progress ───────────────────────────────────────────────
  const totals = useMemo(() => {
    if (datasetState.status !== "ready") return null;
    const data = datasetState.data;
    let pop = 0;
    let pop21 = 0;
    let area = 0;
    let length = 0;
    let elevation = 0;
    let prominence = 0;
    for (const id of guesses) {
      const m = data.byId.get(id);
      if (!m) continue;
      pop += m.pop;
      pop21 += m.pop21;
      area += m.area;
      length += m.length ?? 0;
      elevation += m.elevation ?? 0;
      prominence += m.prominence ?? 0;
    }
    return {
      count: guesses.size,
      countTotal: data.raw.count,
      pop,
      popTotal: data.raw.totalPopulation,
      pop21,
      pop21Total: data.raw.totalPopulation2021,
      area,
      areaTotal: data.raw.totalAreaM2,
      length,
      lengthTotal: data.raw.totalLengthM ?? 0,
      elevation,
      elevationTotal: data.raw.totalElevationM ?? 0,
      prominence,
      prominenceTotal: data.raw.totalProminenceM ?? 0,
    };
  }, [guesses, datasetState]);

  const categoryProgress = useMemo(() => {
    if (datasetState.status !== "ready" || mode !== "chkonp") return null;
    const counts = Object.fromEntries(
      PROTECTED_AREA_KINDS.map((kind) => [kind, { guessed: 0, total: 0 }]),
    ) as Record<ProtectedAreaKind, { guessed: number; total: number }>;
    for (const e of datasetState.data.raw.entities) {
      const kind = e.kind;
      if (!kind) continue;
      counts[kind].total += 1;
      if (guesses.has(e.id)) counts[kind].guessed += 1;
    }
    return counts;
  }, [guesses, datasetState, mode]);

  // ── Sorted guess list ───────────────────────────────────────────────
  const sortedGuesses = useMemo<Entity[]>(() => {
    if (datasetState.status !== "ready") return [];
    const byId = datasetState.data.byId;
    const secondary = datasetState.data.descriptor.secondaryStat;
    const list: Entity[] = [];
    if (sortMode === "recent") {
      for (const id of order) {
        const m = byId.get(id);
        if (m) list.push(m);
      }
    } else {
      for (const id of guesses) {
        const m = byId.get(id);
        if (m) list.push(m);
      }
      if (sortMode === "population") {
        // "By size" is overloaded per descriptor: population for
        // obce/okresy/praha, area for parks/CHKO, length for rivers.
        if (secondary === "area") {
          list.sort((a, b) => b.area - a.area);
        } else if (secondary === "length") {
          list.sort((a, b) => (b.length ?? 0) - (a.length ?? 0));
        } else if (secondary === "elevation") {
          list.sort((a, b) => (b.elevation ?? 0) - (a.elevation ?? 0));
        } else if (secondary === "prominence") {
          list.sort((a, b) => (b.prominence ?? 0) - (a.prominence ?? 0));
        } else {
          list.sort((a, b) => b.pop - a.pop);
        }
      } else {
        list.sort((a, b) => a.name.localeCompare(b.name, "cs"));
      }
    }
    return list;
  }, [guesses, order, sortMode, datasetState]);

  const descriptor = datasetState.status === "ready" ? datasetState.data.descriptor : null;
  const ready = datasetState.status === "ready" && hydratedFor === mode;
  const canPlay = ready && !finished;
  const inDaily = view === "daily";

  // Refresh the daily-button glow when returning from a solved puzzle.
  useEffect(() => {
    if (view === "daily") setDailyGlow(isTodayDailyUnplayed());
  }, [view, dailyOffset]);

  // Keep the active mode tab visible in the horizontally scrollable strip.
  useEffect(() => {
    if (inDaily) return;
    const active = document.querySelector<HTMLElement>(
      ".mode-switch button.active",
    );
    active?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [mode, inDaily]);

  const openDaily = useCallback(() => {
    setDailyTestMode(false);
    setDailyOffset(todayOffset());
    setView("daily");
  }, []);

  const openMap = useCallback((m: Mode) => {
    setDailyTestMode(false);
    setMode(m);
    setView("map");
  }, []);

  const changeDailyOffset = useCallback(
    (o: number) => {
      if (dailyTestMode) {
        const max = dailyState.status === "ready" ? dailyState.data.places.length - 1 : 0;
        const next = Math.max(0, Math.min(o, max));
        setDailyOffset(next);
        window.history.replaceState(null, "", `/daily-test/${next}`);
        return;
      }
      setDailyOffset(Math.max(0, Math.min(o, todayOffset())));
    },
    [dailyTestMode, dailyState],
  );

  // ── Global keyboard capture ─────────────────────────────────────────
  // Panning/zooming the map steals focus onto the canvas. Without
  // this, the player has to click the input before every guess.
  // Printable keys, Backspace, and Enter are routed into the guess
  // field from anywhere on the page except other form controls.
  useEffect(() => {
    if (!canPlay) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const target = e.target;
      if (target instanceof HTMLElement) {
        // Already in our input – native behaviour is fine.
        if (target === inputRef.current) return;
        // Don't hijack other editable controls (borders checkbox, etc.).
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        if (target.isContentEditable) return;
      }

      const focusInput = () => inputRef.current?.focus();

      if (e.key === "Enter") {
        e.preventDefault();
        submit(inputValueRef.current);
        focusInput();
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        setInput((v) => v.slice(0, -1));
        focusInput();
        return;
      }

      // Single printable character (letters, digits, space, diacritics…).
      if (e.key.length === 1) {
        e.preventDefault();
        setInput((v) => v + e.key);
        focusInput();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canPlay, submit]);

  return (
    <div className={"app" + (inDaily ? " app-daily" : "")}>
      <header className="header">
        <div className="brand">
          <h1>Hádej Česko</h1>
          <button
            type="button"
            className={
              "daily-btn" +
              (inDaily ? " active" : "") +
              (dailyGlow && !inDaily ? " daily-btn-glow" : "")
            }
            onClick={openDaily}
            title="Denní hádanka města"
          >
            Denní
          </button>
        </div>
        <div className="header-modes">
          <div className="mode-switch" role="tablist" aria-label="Herní režim">
            {(Object.keys(DATASETS) as Mode[]).map((m) => (
              <button
                key={m}
                role="tab"
                aria-selected={!inDaily && mode === m}
                className={!inDaily && mode === m ? "active" : ""}
                onClick={() => openMap(m)}
              >
                {DATASETS[m].modeLabel}
              </button>
            ))}
          </div>
        </div>
        <div className="header-actions">
          {!inDaily && (
            <>
              {/* Borders toggle is irrelevant for non-polygon datasets
                  (no shared boundaries to outline), so it's hidden for
                  metro / rivers. */}
              {(descriptor?.geometryKind ?? "polygon") === "polygon" && (
                <label
                  className="toggle"
                  title={`Zobrazit / skrýt hranice (${descriptor?.modeLabel ?? ""})`}
                >
                  <input
                    type="checkbox"
                    checked={bordersVisible}
                    onChange={(e) => setBordersVisible(e.target.checked)}
                  />
                  <span>Hranice</span>
                </label>
              )}
              {finished ? (
                <span
                  className="badge-finished"
                  title="Hra byla ukončena. Najetím na mapu si můžeš prohlédnout, co ti uniklo."
                >
                  ✓ Hotovo
                </span>
              ) : (
                <button
                  className="btn"
                  onClick={finish}
                  disabled={!ready}
                  title="Ukončit hru a zobrazit, co jsi uhodl"
                >
                  Ukončit
                </button>
              )}
              <button
                className="btn btn-danger"
                onClick={reset}
                disabled={guesses.size === 0 && !finished}
                title="Resetovat hru"
              >
                Resetovat
              </button>
            </>
          )}
        </div>
      </header>

      {inDaily ? (
        <DailyView
          dailyState={dailyState}
          offset={dailyOffset}
          onOffsetChange={changeDailyOffset}
          onSolved={() => setDailyGlow(isTodayDailyUnplayed())}
          testMode={dailyTestMode}
        />
      ) : (
        <>
      <MapView
        // Remount on mode change: each dataset has its own source/layers,
        // and tearing down + recreating is cheaper to reason about than
        // hot-swapping the geojson source mid-flight.
        key={mode}
        geojsonUrl={DATASETS[mode].geojsonUrl}
        guesses={guesses}
        flyTo={flyTo}
        bordersVisible={bordersVisible}
        revealAll={finished}
        geometryKind={DATASETS[mode].geometryKind ?? "polygon"}
        pointVariant={DATASETS[mode].pointVariant}
        initialBounds={
          datasetState.status === "ready" ? datasetState.data.raw.bounds : undefined
        }
        categoryProperty={DATASETS[mode].categoryProperty}
        categoryColors={DATASETS[mode].categoryColors}
        countryBackground={DATASETS[mode].countryBackground}
      />

      <aside className="sidebar">
        <div className="input-row">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <input
              ref={inputRef}
              className={"input" + (bad ? " input-bad" : "")}
              placeholder={
                !ready
                  ? "Načítám data…"
                  : finished
                    ? (descriptor?.finishedPlaceholder ?? "Hra ukončena.")
                    : (descriptor?.inputPlaceholder ?? "")
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!canPlay}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              aria-label={descriptor?.inputPlaceholder ?? ""}
            />
          </form>
          <div className="input-hint">
            {datasetState.status === "loading" &&
              `Stahuji seznam ${DATASETS[mode].countNoun}…`}
            {datasetState.status === "error" && (
              <span style={{ color: "var(--warn)" }}>Chyba: {datasetState.message}</span>
            )}
          </div>
        </div>

        {totals && descriptor && (
          <div className="stats">
            <Stat
              label={descriptor.statCountLabel}
              value={totals.count}
              total={totals.countTotal}
              suffix=""
            />
            {descriptor.secondaryStat === "population2021" && (
              <Stat
                label="Obyvatel"
                value={totals.pop21}
                total={totals.pop21Total}
                suffix=""
              />
            )}
            {descriptor.secondaryStat === "area" && descriptor.mode !== "chkonp" && (
              <Stat
                // m² → km², rounded to integers; areas of these
                // categories are big enough that decimals just add
                // noise.
                label="Rozloha"
                value={Math.round(totals.area / 1e6)}
                total={Math.round(totals.areaTotal / 1e6)}
                suffix=" km²"
              />
            )}
            {descriptor.mode === "chkonp" && categoryProgress && (
              <div className="stats-categories">
                {PROTECTED_AREA_KINDS.map((kind) => (
                  <Stat
                    key={kind}
                    label={kind}
                    title={PROTECTED_AREA_STAT_LABELS[kind]}
                    value={categoryProgress[kind].guessed}
                    total={categoryProgress[kind].total}
                    color={PROTECTED_AREA_COLORS[kind].fill}
                    compact
                  />
                ))}
              </div>
            )}
            {descriptor.secondaryStat === "length" && (
              <Stat
                label="Délka řek"
                value={Math.round(totals.length / 1000)}
                total={Math.round(totals.lengthTotal / 1000)}
                suffix=" km"
              />
            )}
            {descriptor.secondaryStat === "elevation" && (
              <Stat
                label="Výška"
                value={totals.elevation}
                total={totals.elevationTotal}
                suffix=" m"
              />
            )}
            {descriptor.secondaryStat === "prominence" && (
              <Stat
                label="Prominence"
                value={totals.prominence}
                total={totals.prominenceTotal}
                suffix=" m"
              />
            )}
            {/* secondaryStat === "none" → just the count bar (metro
                has no meaningful aggregate metric beyond station count). */}
          </div>
        )}

        <div className="list-head">
          <span>{descriptor?.listTitle ?? "Uhádnuté"}</span>
          <button
            className="sort-toggle"
            onClick={() =>
              setSortMode((s) => {
                // Metro has no aggregate metric, so the dial only
                // cycles recent ↔ alpha for it.
                if (descriptor?.secondaryStat === "none") {
                  return s === "recent" ? "alpha" : "recent";
                }
                return s === "recent"
                  ? "population"
                  : s === "population"
                    ? "alpha"
                    : "recent";
              })
            }
            title="Změnit řazení"
          >
            ↕{" "}
            {sortMode === "recent"
              ? "podle pořadí"
              : sortMode === "population"
                ? descriptor?.secondaryStat === "area"
                  ? "podle rozlohy"
                  : descriptor?.secondaryStat === "length"
                    ? "podle délky"
                    : descriptor?.secondaryStat === "elevation"
                      ? "podle výšky"
                      : descriptor?.secondaryStat === "prominence"
                        ? "podle prominence"
                        : "podle počtu obyvatel"
                : "abecedně"}
          </button>
        </div>

        <div className="list">
          {sortedGuesses.map((m) => {
            const isMetro = descriptor?.mode === "metro";
            const isRiver = descriptor?.mode === "reky";
            const isPeak = descriptor?.pointVariant === "peak";
            const isProtected = descriptor?.mode === "chkonp";
            const isBrno = descriptor?.mode === "brno";
            const sub = isMetro || isRiver
              ? null
              : isPeak
                ? m.range || null
                : m.okres
                  ? `okres ${m.okres} · ${m.kraj}`
                  : m.kraj;
            const kindColors = m.kind ? PROTECTED_AREA_COLORS[m.kind] : null;
            return (
              <div
                key={m.id}
                className={"guess" + (m.id === latestId ? " is-latest" : "")}
                onClick={() => setFlyTo({ bbox: m.bbox, key: Date.now() })}
                title="Najet na mapě"
              >
                <div className="name">{m.name}</div>
                <div className="pop">
                  {isMetro ? (
                    // Metro rows show one coloured chip per line the
                    // station belongs to (so transfers display both).
                    <span className="line-chips">
                      {[...(m.lines ?? "")].map((c) => (
                        <span
                          key={c}
                          className="line-chip"
                          style={{
                            background:
                              METRO_LINE_COLORS[c as "A" | "B" | "C"] ?? "#888",
                          }}
                        >
                          {c}
                        </span>
                      ))}
                    </span>
                  ) : isProtected && m.kind ? (
                    <span
                      className="line-chip"
                      style={{ background: kindColors?.fill ?? "#888" }}
                    >
                      {m.kind}
                    </span>
                  ) : isRiver ? (
                    `${Math.round((m.length ?? 0) / 1000).toLocaleString("cs")} km`
                  ) : isBrno ? null : isPeak ? (
                    descriptor?.secondaryStat === "prominence"
                      ? `${(m.prominence ?? 0).toLocaleString("cs")} m`
                      : `${(m.elevation ?? 0).toLocaleString("cs")} m`
                  ) : m.pop > 0 ? (
                    // Obce/okresy keep the familiar population figure;
                    // parks (pop=0) fall through to km².
                    m.pop.toLocaleString("cs")
                  ) : (
                    `${Math.round(m.area / 1e6).toLocaleString("cs")} km²`
                  )}
                </div>
                {sub && (
                  <div className="sub" style={{ gridColumn: "1 / -1" }}>
                    {sub}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
        </>
      )}
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
  total: number;
  suffix?: string;
  /** Override the progress bar fill colour (e.g. per protected-area kind). */
  color?: string;
  compact?: boolean;
  title?: string;
}

function Stat({
  label,
  value,
  total,
  suffix = "",
  color,
  compact = false,
  title,
}: StatProps) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className={"stat" + (compact ? " stat-compact" : "")} title={title}>
      <div className="stat-head">
        <span className="stat-label">{label}</span>
        <span className="stat-value">
          <span className="num">{value.toLocaleString("cs")}</span>
          {suffix}
          <span className="total">
            {" / "}
            {total.toLocaleString("cs")}
            {suffix} · {pct.toFixed(pct < 1 ? 2 : 1)} %
          </span>
        </span>
      </div>
      <div className="bar" aria-hidden>
        <div style={{ width: `${pct}%`, background: color ?? "var(--accent)" }} />
      </div>
    </div>
  );
}
